import { useState, useEffect } from 'react';
import { Button } from './Button';
import { Icon } from './Icon';
import { StatusPill, SentPill } from './StatusPill';
import type { ReportRow } from '../types';
import { fmt } from '../lib/format';
import { missingFields } from '../lib/review';

// FinanceReportRow keys tracked by changed-since-sent detection → human labels.
const CHANGED_FIELD_LABELS: Record<string, string> = {
  accountName: 'Account',
  affiliateFullName: 'Affiliate',
  coachee: 'Coachee',
  dateIncurred: 'Date incurred',
  hours: 'Hours',
  affiliatePaidHourlyRate: 'Affiliate rate',
  clientHourlyFee: 'Client hourly fee',
  cogs: 'COGS',
  hoursRevenue: 'Hours revenue',
};

interface Props {
  row: ReportRow;
  onClose(): void;
}

const ENRICHED_LABELS: Record<string, string> = {
  status: 'Status',
  netSuiteProjectId: 'NetSuite Project ID',
  accountName: 'Account',
  engagementTemplate: 'Template',
  coachee: 'Coachee',
  coacheeLastName: 'Coachee last name',
  truncatedName: 'Truncated name',
  internalIdProject: 'Internal ID (project)',
  affiliatePaidHourlyRate: 'Affiliate rate',
  netSuiteOpportunityNumber: 'NetSuite opp #',
  sowEngagementStartDate: 'SOW start',
  sowEngagementEndDate: 'SOW end',
  totalHoursBudget: 'Hours budget',
  clientHourlyFee: 'Client hourly fee',
  totalClientFeeBudget: 'Client fee budget',
};

export function RowDrawer({ row, onClose }: Props): JSX.Element {
  const [copied, setCopied] = useState(false);
  const utilization = row.hBudget ? (row.hIncurred ?? 0) / row.hBudget : null;

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-hd">
          <div>
            <div className="eb">Session · {row.id.toUpperCase()}</div>
            <div className="nm">{row.account ?? <span className="em">—</span>}</div>
            <div className="d-meta" style={{ marginTop: 6, display: 'flex', gap: 12, alignItems: 'center' }}>
              <StatusPill value={row.status} />
              <span className="d-tnum">{fmt.date(row.dateInc)}</span>
              <span>·</span>
              <span>{fmt.hrs(row.hours)} h</span>
            </div>
          </div>
          <div className="collapse-btn" onClick={onClose} title="Close (Esc)">
            <Icon name="x" size={16} />
          </div>
        </div>
        <div className="drawer-bd">
          {row.enrichedFields && row.enrichedFields.length > 0 && (
            <div className="drawer-section">
              <span
                className="pill"
                style={{ background: 'rgba(45,106,158,0.12)', color: 'var(--navy)' }}
              >
                <span className="pdot" />
                Filled from Coaching.com — verify:{' '}
                {row.enrichedFields.map((f) => ENRICHED_LABELS[f] ?? f).join(', ')}
              </span>
            </div>
          )}
          {missingFields(row).length > 0 && (
            <div className="drawer-section">
              <span
                className="pill"
                style={{ background: 'rgba(217,83,79,0.12)', color: '#b8430a' }}
              >
                <span className="pdot" />
                Missing — complete in Salesforce: {missingFields(row).join(', ')}
              </span>
            </div>
          )}
          <div className="drawer-section">
            <div className="drawer-section-h">Engagement</div>
            <dl className="kv">
              <dt>Account</dt><dd>{row.account ?? <span className="em">—</span>}</dd>
              <dt>Template</dt><dd>{row.template ?? <span className="em">—</span>}</dd>
              <dt>Affiliate</dt><dd>{row.affiliate ?? <span className="em">—</span>}</dd>
              <dt>Coachee</dt><dd>{row.coachee ?? <span className="em">—</span>}</dd>
              <dt>SOW dates</dt><dd className="d-tnum">{fmt.date(row.sowStart)} → {fmt.date(row.sowEnd)}</dd>
              <dt>Milestone</dt><dd>{row.milestone}</dd>
            </dl>
          </div>

          <div className="drawer-section">
            <div className="drawer-section-h">Hours &amp; fees</div>
            <dl className="kv">
              <dt>This session</dt><dd className="d-tnum">{fmt.hrs(row.hours)} h</dd>
              <dt>Hours budget</dt><dd className="d-tnum">{fmt.hrs(row.hBudget)} h</dd>
              <dt>Hours incurred</dt>
              <dd>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="d-tnum">{fmt.hrs(row.hIncurred)} h</span>
                  {utilization != null && utilization >= 1.0 && (
                    <span className="pill" style={{ background: 'rgba(184,67,10,0.12)', color: 'var(--warn)' }}>
                      <span className="pdot" />{Math.round(utilization * 100)}% of budget
                    </span>
                  )}
                </div>
                <div style={{ height: 4, background: 'rgba(10,21,40,0.08)', marginTop: 8, position: 'relative' }}>
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    width: Math.min(100, (utilization ?? 0) * 100) + '%',
                    background: (utilization ?? 0) >= 1.0 ? 'var(--warn)' : 'var(--ok)',
                  }} />
                </div>
              </dd>
              <dt>Affiliate rate</dt><dd>{fmt.curFull(row.rate)}</dd>
              <dt>Client hourly fee</dt><dd>{fmt.curFull(row.clientFee)}</dd>
              <dt>Client fee budget</dt><dd style={{ fontWeight: 600, color: 'var(--navy)' }}>{fmt.curFull(row.feeBudget)}</dd>
              <dt>COGS</dt><dd className="d-tnum">{row.cogs != null ? fmt.curFull(row.cogs) : <span className="em">—</span>}</dd>
              <dt>Hours revenue</dt><dd className="d-tnum">{row.hoursRevenue != null ? fmt.curFull(row.hoursRevenue) : <span className="em">—</span>}</dd>
              <dt>Affiliate rate % of client</dt><dd className="d-tnum">{row.ratePct != null ? (row.ratePct * 100).toFixed(1) + '%' : <span className="em">—</span>}</dd>
              <dt>Over budget?</dt><dd>{row.overBudget ?? <span className="em">—</span>}</dd>
            </dl>
          </div>

          <div className="drawer-section">
            <div className="drawer-section-h">NetSuite prep</div>
            <dl className="kv">
              <dt>Vendor name</dt><dd>{row.vendorName ?? <span className="em">—</span>}</dd>
              <dt>Internal ID (project)</dt><dd className="mono">{row.internalId ?? <span className="em">—</span>}</dd>
              <dt>Truncated name</dt><dd>{row.truncatedName ?? <span className="em">—</span>}</dd>
              <dt>Coachee last name</dt>
              <dd>
                {row.coacheeLast ?? <span className="em">—</span>}
                {row.coacheeLastDerived && row.coacheeLast && (
                  <span className="pill" style={{ marginLeft: 8, background: 'rgba(184,67,10,0.12)', color: 'var(--warn)' }}>
                    <span className="pdot" />derived
                  </span>
                )}
              </dd>
              <dt>Expiration date</dt><dd className="d-tnum">{row.expiration ? fmt.date(row.expiration) : <span className="em">—</span>}</dd>
              <dt>Line memo</dt>
              <dd className="mono" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {row.lineMemo ?? <span className="em">—</span>}
              </dd>
              <dt>Reference memo</dt>
              <dd className="mono" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {row.refMemo ?? <span className="em">—</span>}
                {row.refMemoLen != null && (
                  <span
                    style={{ marginLeft: 8, color: row.refMemoLen > 39 ? 'var(--warn)' : 'var(--mute)' }}
                  >
                    ({row.refMemoLen} chars{row.refMemoLen > 39 ? ' — over 39' : ''})
                  </span>
                )}
              </dd>
            </dl>
          </div>

          <div className="drawer-section">
            <div className="drawer-section-h">Sent to finance</div>
            <dl className="kv">
              <dt>Status</dt>
              <dd><SentPill value={row.sentStatus ?? 'unsent'} /></dd>
              {row.sentAt && (
                <>
                  <dt>Last sent</dt><dd className="d-tnum">{fmt.dateTime(row.sentAt)}</dd>
                  <dt>Recorded via</dt>
                  <dd>{row.sentSource === 'paste' ? 'Pasted session IDs' : 'File upload'}</dd>
                </>
              )}
              {(row.sentChangedFields?.length ?? 0) > 0 && (
                <>
                  <dt>Differs since</dt>
                  <dd style={{ color: 'var(--warn)' }}>
                    {row.sentChangedFields!.map((f) => CHANGED_FIELD_LABELS[f] ?? f).join(', ')} no longer match what
                    finance last received.
                  </dd>
                </>
              )}
              {row.sentStatus === 'sent' && row.sentSource === 'paste' && (
                <>
                  <dt>Note</dt>
                  <dd style={{ color: 'var(--mute)' }}>
                    Recorded from pasted IDs (no file), so changed-since-sent detection doesn't apply.
                  </dd>
                </>
              )}
            </dl>
          </div>

          <div className="drawer-section">
            <div className="drawer-section-h">Source IDs</div>
            <dl className="kv">
              <dt>NetSuite opp #</dt><dd className="mono">{row.oppNum}</dd>
              <dt>NetSuite opp ID</dt><dd className="mono">{row.oppId}</dd>
              <dt>NetSuite project</dt><dd className="mono">{row.projectId}</dd>
              <dt>CC engagement</dt><dd className="mono">{row.engagementId}</dd>
              <dt>CC session</dt><dd className="mono">{row.sessionId}</dd>
            </dl>
          </div>

          <div className="drawer-section">
            <div className="drawer-section-h">Metadata</div>
            <dl className="kv">
              <dt>Date incurred</dt><dd className="d-tnum">{fmt.date(row.dateInc)}</dd>
              <dt>Logged at</dt><dd className="d-tnum">{fmt.date(row.created)} · 14:32</dd>
              <dt>Raw status</dt><dd className="mono">{row.status.toLowerCase()}</dd>
            </dl>
          </div>
        </div>
        <div className="drawer-foot">
          <Button
            variant="secondary"
            icon={copied ? 'check' : 'link'}
            onClick={() => {
              navigator.clipboard?.writeText(window.location.href);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
          >
            {copied ? 'Copied' : 'Copy session link'}
          </Button>
          <div style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
